import { toast } from "react-toastify";
import { BASE_URL } from "../../constant";
import { getLanguage } from "react-switch-lang";

export function areObjectsEqual(obj1, obj2) {
  const keys = Object.keys(obj1);
  for (let key of keys) {
    if (!obj1[key] || !obj2[key]) continue;
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  return true;
}

const FetchHeaders = {
  "Content-Type": "application/json",
};
async function Fetch({ url, method, body, headers, token, ...rest }) {
  try {
    const response = await fetch(url, { method, body, ...rest });
    if (response.status >= 200 && response.status < 300) {
      return response.json();
    } else {
      console.log("FetchError ->  response", response);
    }
  } catch (error) {
    console.log("FetchError -> error", error);
  }
}

export async function fetchAllCartData() {
  try {
    const user_id = localStorage.getItem("user_id");
    
    // Check if this is an event booking flow (booking_event exists)
    // For event bookings, NEVER show shipping address
    const bookingEvent = user_id && user_id > 0 
      ? localStorage.getItem('booking_event')
      : localStorage.getItem('booking_event_guest');
    
    const isEventCheckout = !!bookingEvent;

    if (user_id && user_id > 0) {
      // Logged-in user: fetch from backend
      const productsUrl = `${BASE_URL}cart/getAllMyCartData/${user_id}/${getLanguage()}`;
      const magazinesUrl = `${BASE_URL}cart/getAllMyCartDataForMagazine/${user_id}/${getLanguage()}`;
      const eventsUrl = `${BASE_URL}cart/getAllMyCartDataForEvents/${user_id}/${getLanguage()}`;
      const machineTestEventsUrl = `${BASE_URL}cart/getAllMyCartDataForMachineTestEvents/${user_id}/${getLanguage()}`;

      const productsRequest = Fetch({ url: productsUrl, method: "GET" });
      const magazinesRequest = Fetch({ url: magazinesUrl, method: "GET" });
      const eventsRequest = Fetch({ url: eventsUrl, method: "GET" });
      const machineTestEventsRequest = Fetch({ url: machineTestEventsUrl, method: "GET" });

      const [products, magazines, events, machineTestEvents] = await Promise.all([
        productsRequest,
        magazinesRequest,
        eventsRequest,
        machineTestEventsRequest,
      ]);

      // Shipping is only required for products and magazines, NOT for events
      // If this is an event checkout (booking_event exists), NEVER show shipping
      const shipping = isEventCheckout ? false : (products?.length || magazines?.length ? true : false);

      return { cart: { products, magazines, events, machineTestEvents }, billing: true, shipping };
    } else {
      // Guest user: load from localStorage
      const guestProducts = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
      const guestMagazines = JSON.parse(localStorage.getItem('guest_cart_magazines') || '[]');
      const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
      const guestMachineTests = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
      
      // Transform guest data to match backend structure if needed
      const products = guestProducts;
      const magazines = guestMagazines.map(mag => ({
        ...mag,
        magazine_title: mag.title,
        subscription_type: mag.subscription_type || 'Subscription',
        package_title: mag.package_title || 'Package',
        donation_contribution: mag.donation_contribution || mag.price || 0,
      }));
      
      // Transform guest events to grouped structure
      const eventsGrouped = {};
      const machineTestEventsGrouped = {};
      
      guestEvents.forEach(event => {
        const eventType = event.event_type || 'Event';
        const isMachineTest = event.event_type === 'Machine Test' || event.event_type_id === 3;
        
        const targetGroup = isMachineTest ? machineTestEventsGrouped : eventsGrouped;
        
        if (!targetGroup[eventType]) {
          targetGroup[eventType] = {
            event_type: eventType,
            event_type_id: event.event_type_id || 1,
            arr_cart: []
          };
        }
        targetGroup[eventType].arr_cart.push({
          event_id: event.event_id,
          schedule_id: event.schedule_id,
          title: event.title,
          parent: event.parent || 0,
          child: event.child || 0,
          first_price: event.price || 0,
          second_price: event.child_price || event.price || 0,
          venue_address: event.venue_address || '',
        });
      });
      
      // Add machine tests from guest_mt_cart
      guestMachineTests.forEach(mt => {
        const eventType = 'Machine Test';
        if (!machineTestEventsGrouped[eventType]) {
          machineTestEventsGrouped[eventType] = {
            event_type: eventType,
            event_type_id: 3,
            arr_cart: []
          };
        }
        machineTestEventsGrouped[eventType].arr_cart.push({
          event_id: mt.event_id,
          schedule_id: mt.schedule_id,
          title: mt.title,
          parent: mt.parent || 0,
          child: mt.child || 0,
          first_price: mt.price || mt.first_price || 0,
          second_price: mt.child_price || mt.second_price || mt.price || 0,
          venue_address: mt.venue_address || '',
        });
      });
      
      const events = Object.values(eventsGrouped);
      const machineTestEvents = Object.values(machineTestEventsGrouped);
      
      // Shipping is required for products and magazines
      // If this is an event checkout (booking_event exists), NEVER show shipping
      const shipping = isEventCheckout ? false : (guestProducts?.length || guestMagazines?.length ? true : false);

      return { cart: { products, magazines, events, machineTestEvents }, billing: true, shipping };
    }
  } catch (error) {
    console.log("fetchAllCartData -> error", error);
    // Return empty cart structure on error
    return { cart: { products: [], magazines: [], events: [], machineTestEvents: [] }, billing: true, shipping: false };
  }
}

export async function fetchCountries() {
  try {
    const url = `${BASE_URL}getAllCountry`;
    const data = await Fetch({ url, method: "GET" });
    return data;
  } catch (error) {
    console.log("fetchCountries -> error", error);
  }
}

export async function fetchStatesByCountry(countryId) {
  try {
    const url = `${BASE_URL}getStateCountryWise/${countryId}`;
    const data = await Fetch({ url, method: "GET" });
    return data;
  } catch (error) {
    console.log("fetchStatesByCountry -> error", error);
  }
}

export async function fetchCitiesByState(stateId) {
  try {
    const url = `${BASE_URL}getCityStateWise/${stateId}`;
    const data = await Fetch({ url, method: "GET" });
    return data;
  } catch (error) {
    console.log("fetchCitiesByState -> error", error);
  }
}

/**
 * Fetches Billing address for the user.
 * @param {callback} callback - The callback function to handle errors.
 */
export async function fetchBillingAddressById(callback) {
  try {
    const user_id = localStorage.getItem("user_id");
    
    if (user_id && user_id > 0) {
      // Logged-in user: fetch from backend
      const url = `${BASE_URL}users/getBillingAddress/1/${user_id}`;
      const data = await Fetch({ url, method: "GET" });
      if (callback) callback(null, data?.[0]);
      return data?.[0];
    } else {
      // Guest user: load from localStorage if available
      const savedAddress = localStorage.getItem('guest_billing_address');
      const data = savedAddress ? JSON.parse(savedAddress) : null;
      if (callback) callback(null, data);
      return data;
    }
  } catch (error) {
    console.log("fetchBillingAddressById -> error", error);
    if (callback) callback(error, null);
  }
}

/**
 * Fetches Shipping address for the user.
 * @param {callback} callback - The callback function to handle errors.
 */
export async function fetchShippingAddressById(callback) {
  try {
    const user_id = localStorage.getItem("user_id");
    
    if (user_id && user_id > 0) {
      // Logged-in user: fetch from backend
      const url = `${BASE_URL}users/getBillingAddress/2/${user_id}`;
      const data = await Fetch({ url, method: "GET" });
      if (callback) callback(null, data?.[0]);
      return data?.[0];
    } else {
      // Guest user: load from localStorage if available
      const savedAddress = localStorage.getItem('guest_shipping_address');
      const data = savedAddress ? JSON.parse(savedAddress) : null;
      if (callback) callback(null, data);
      return data;
    }
  } catch (error) {
    console.log("fetchShippingAddressById -> error", error);
    if (callback) callback(error, null);
  }
}

/**
 * Saves Billing address for the user.
 * @param {object} payload - The address payload.
 * @param {callback} callback - The callback function to handle errors.
 */
export async function saveBillingAddressById(
  payload,
  callback,
  onSaveCallback,
  onErrorCallback
) {
  try {
    const user_id = localStorage.getItem("user_id");
    
    if (user_id && user_id > 0) {
      // Logged-in user: save to backend
      const url = `${BASE_URL}users/addAddress/1/${user_id}`;
      const data = await Fetch({
        url,
        method: "POST",
        body: JSON.stringify(payload),
        headers: { ...FetchHeaders },
      });
      const { message } = data?.[0];
      if (message) {
        toast(message, { type: toast.TYPE.SUCCESS });
        if (callback) callback(null, data?.[0]);
        return data?.[0];
      }
    } else {
      // Guest user: save to localStorage temporarily
      localStorage.setItem('guest_billing_address', JSON.stringify(payload));
      // Toast removed for guest users - silent save
      if (callback) callback(null, payload);
      return payload;
    }
  } catch (error) {
    console.log("saveBillingAddressById -> error", error);
    const { message } = error;
    if (message) {
      toast(message, { type: toast.TYPE.ERROR });
      if (callback) callback(error, null);
    }
    console.log("HandleError");
    throw error;
  }
}

/**
 * Saves Shipping address for the user.
 * @param {object} payload - The address payload.
 * @param {callback} callback - The callback function to handle errors.
 */
export async function saveShippingAddressById(
  payload,
  callback
  // onSaveCallback,
  // onErrorCallback
) {
  try {
    const user_id = localStorage.getItem("user_id");
    
    if (user_id && user_id > 0) {
      // Logged-in user: save to backend
      const url = `${BASE_URL}users/addAddress/2/${user_id}`;
      const data = await Fetch({
        url,
        method: "POST",
        body: JSON.stringify(payload),
        headers: { ...FetchHeaders },
      });
      const { message } = data?.[0];
      if (message) {
        toast(message, { type: toast.TYPE.SUCCESS });
        if (callback) callback(null, data?.[0]);
        return data?.[0];
      }
    } else {
      // Guest user: save to localStorage temporarily
      localStorage.setItem('guest_shipping_address', JSON.stringify(payload));
      // Toast removed for guest users - silent save
      if (callback) callback(null, payload);
      return payload;
    }
  } catch (error) {
    console.log("saveShippingAddressById -> error", error);
    const { message } = error;
    if (message) {
      toast(message, { type: toast.TYPE.ERROR });
      if (callback) callback(error, null);
    }
    console.log("HandleError");
    throw error;
  }
}

//
//
/**
 *
 */
function callback(error, data) {}
